import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const TYPES      = ['واجب', 'مشروع', 'اختبار'];
const PRIORITIES = ['منخفضة', 'متوسطة', 'عالية'];
const STATUSES   = ['لم يبدأ', 'جاري', 'مكتمل'];

const DAYS_AR = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو',
                   'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

const TYPE_COLORS     = { واجب: '#28E3DA', مشروع: '#F1C267', اختبار: '#FA8686' };
const PRIORITY_COLORS = { منخفضة: '#5FCC9C', متوسطة: '#F1C267', عالية: '#FA8686' };
const STATUS_COLORS   = { 'لم يبدأ': 'rgba(255,255,255,0.45)', جاري: '#60A5FA', مكتمل: '#5FCC9C' };

const baseInputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  colorScheme: 'dark',
};
const focusInputStyle = {
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(40,227,218,0.4)',
  colorScheme: 'dark',
};

const EMPTY = {
  subject: '', title: '', description: '',
  type: 'واجب', priority: 'متوسطة', status: 'لم يبدأ',
  due_date: '', week_num: '',
};


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
        {label}
      </label>
      {children}
    </div>
  );
}


function StyledInput({ value, onChange, placeholder, type = 'text', ...rest }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className="w-full h-10 rounded-xl px-3 text-sm text-white text-right outline-none"
      style={focused ? focusInputStyle : baseInputStyle}
      dir="rtl"
      {...rest}
    />
  );
}


function PillSelect({ options, value, onChange, colors = {} }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((opt) => {
        const active = value === opt;
        const col    = colors[opt] || '#28E3DA';
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className="px-3 py-1.5 rounded-full text-xs font-bold transition-all"
            style={
              active
                ? { background: `${col}25`, border: `1px solid ${col}`, color: col }
                : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)' }
            }
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}


function WeekSelect({ value, onChange, numWeeks }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);


  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const weeks = Array.from({ length: numWeeks }, (_, i) => i + 1);
  const label = value ? `أسبوع ${value}` : 'اختر الأسبوع';

  return (
    <div ref={ref} className="relative">
      {             }
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-10 rounded-xl px-3 text-sm text-right flex items-center justify-between outline-none transition-all"
        style={open ? focusInputStyle : baseInputStyle}
        dir="rtl"
      >
        <span style={{ color: value ? '#fff' : 'rgba(255,255,255,0.3)' }}>{label}</span>
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
        >
          <path d="M2 4L6 8L10 4" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {                    }
      {open && (
        <div
          className="absolute top-full mt-1.5 left-0 right-0 z-[99999] rounded-xl p-3 overflow-y-auto"
          style={{
            background: 'rgb(8,20,35)',
            border: '1px solid rgba(40,227,218,0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            maxHeight: 220,
          }}
        >
          {                  }
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="w-full text-right text-xs px-2 py-1.5 rounded-lg mb-2 transition-all"
            style={{ color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.04)' }}
          >
            بدون أسبوع محدد
          </button>

          {               }
          <div className="grid grid-cols-4 gap-1.5">
            {weeks.map((w) => {
              const active = String(w) === String(value);
              return (
                <button
                  key={w}
                  type="button"
                  onClick={() => { onChange(String(w)); setOpen(false); }}
                  className="py-1.5 rounded-lg text-xs font-bold transition-all"
                  style={
                    active
                      ? { background: 'rgba(40,227,218,0.15)', color: '#28E3DA', border: '1px solid rgba(40,227,218,0.35)' }
                      : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.07)' }
                  }
                >
                  {w}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function DateField({ value, onChange }) {
  const [focused, setFocused] = useState(false);

  let dayLabel = null;
  if (value) {
    try {
      const d = new Date(`${value}T12:00:00`);
      const daysUntil = Math.ceil((d - new Date().setHours(0,0,0,0)) / 86400000);
      const dayName   = DAYS_AR[d.getDay()];
      const dateStr   = `${d.getDate()} ${MONTHS_AR[d.getMonth()]}`;
      const diff =
        daysUntil < 0  ? `متأخر ${Math.abs(daysUntil)} يوم` :
        daysUntil === 0 ? 'اليوم'  :
        daysUntil === 1 ? 'غداً'   : `بعد ${daysUntil} يوم`;
      const diffColor =
        daysUntil < 0   ? '#FA8686' :
        daysUntil <= 3  ? '#F1C267' : '#5FCC9C';
      dayLabel = { dayName, dateStr, diff, diffColor };
    } catch {            }
  }

  return (
    <div>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full h-10 rounded-xl px-3 text-sm text-white outline-none"
        style={{
          ...(focused ? focusInputStyle : baseInputStyle),
          colorScheme: 'dark',
        }}
        dir="ltr"
      />
      {dayLabel && (
        <div className="flex items-center justify-between mt-1.5 px-1">
          <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {dayLabel.dayName} · {dayLabel.dateStr}
          </span>
          <span className="text-xs font-bold" style={{ color: dayLabel.diffColor }}>
            {dayLabel.diff}
          </span>
        </div>
      )}
    </div>
  );
}




export default function TaskFormModal({ isOpen, onClose, onSave, initial, numWeeks = 16, loading, subjects = [] }) {
  const [form,  setForm]  = useState(EMPTY);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm(
        initial ? {
          subject:     initial.subject     || '',
          title:       initial.title        || '',
          description: initial.description  || '',
          type:        initial.type         || 'واجب',
          priority:    initial.priority     || 'متوسطة',
          status:      initial.status       || 'لم يبدأ',
          due_date:    initial.due_date
            ? (typeof initial.due_date === 'string'
                ? initial.due_date.split('T')[0]
                : new Date(initial.due_date).toISOString().split('T')[0])
            : '',
          week_num:    initial.week_num != null ? String(initial.week_num) : '',
        } : EMPTY
      );
      setError('');
    }
  }, [isOpen, initial]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const set     = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const setPill = (key) => (val) => setForm((p) => ({ ...p, [key]: val }));

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('عنوان المهمة مطلوب'); return; }
    setError('');
    onSave({
      ...form,
      week_num: form.week_num ? parseInt(form.week_num, 10) : null,
      due_date: form.due_date || null,
    });
  }

  const modalBg = {
    background:
      'linear-gradient(0deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02)), ' +
      'radial-gradient(92.12% 184.55% at 50% -55%, rgba(40,227,218,0.15) 0%, rgba(40,227,218,0) 100%), ' +
      'rgb(8,20,35)',
    border: '1px solid rgba(40,227,218,0.2)',
    borderRadius: '18px',
    boxShadow: '0 8px 40px rgba(40,227,218,0.08)',
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      dir="rtl"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto"
        style={modalBg}
        onClick={(e) => e.stopPropagation()}
      >
        {                  }
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div>
            <h2 className="text-base font-bold text-white">
              {initial ? 'تعديل المهمة' : 'إضافة مهمة جديدة'}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {initial ? 'عدّل بيانات المهمة ثم احفظ' : 'أدخل بيانات المهمة الجديدة'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M11 1L1 11" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {                }
        <form onSubmit={handleSubmit} className="p-5 space-y-4">

          {                            }
          <Field label="عنوان المهمة *">
            <StyledInput value={form.title} onChange={set('title')} placeholder="اكتب عنوان المهمة..." />
          </Field>

          <Field label="المادة">
            {subjects.length > 0 ? (
              <div className="relative">
                <StyledInput
                  value={form.subject}
                  onChange={set('subject')}
                  placeholder="اختر مادة أو اكتب..."
                  list="subjects-list"
                />
                <datalist id="subjects-list">
                  {subjects.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
            ) : (
              <StyledInput value={form.subject} onChange={set('subject')} placeholder="مثال: رياضيات ٢" />
            )}
          </Field>

          {                        }
          <div className="grid grid-cols-2 gap-3">
            <Field label="تاريخ التسليم (اليوم بالضبط)">
              <DateField
                value={form.due_date}
                onChange={(v) => setForm((p) => ({ ...p, due_date: v }))}
              />
            </Field>
            <Field label="الأسبوع المرتبط">
              <WeekSelect
                value={form.week_num}
                onChange={(v) => setForm((p) => ({ ...p, week_num: v }))}
                numWeeks={numWeeks}
              />
            </Field>
          </div>

          {                 }
          <Field label="الوصف">
            <textarea
              value={form.description}
              onChange={set('description')}
              placeholder="تفاصيل إضافية (اختياري)..."
              rows={2}
              className="w-full rounded-xl px-3 py-2.5 text-sm text-white text-right outline-none resize-none"
              style={baseInputStyle}
              dir="rtl"
            />
          </Field>

          {          }
          <Field label="النوع">
            <PillSelect options={TYPES} value={form.type} onChange={setPill('type')} colors={TYPE_COLORS} />
          </Field>

          {              }
          <Field label="الأولوية">
            <PillSelect options={PRIORITIES} value={form.priority} onChange={setPill('priority')} colors={PRIORITY_COLORS} />
          </Field>

          {            }
          <Field label="الحالة">
            <PillSelect options={STATUSES} value={form.status} onChange={setPill('status')} colors={STATUS_COLORS} />
          </Field>

          {           }
          {error && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm"
              style={{ background: 'rgba(250,134,134,0.1)', border: '1px solid rgba(250,134,134,0.25)', color: '#FA8686' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              {error}
            </div>
          )}

          {             }
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-10 rounded-xl text-sm font-bold transition-all"
              style={{
                background: 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40,227,218,0.5) 0%, rgba(40,227,218,0) 100%), rgba(40,227,218,0.25)',
                border: '1px solid rgba(40,227,218,0.35)',
                color: '#fff',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? 'جاري الحفظ...' : initial ? 'حفظ التعديلات' : 'إضافة المهمة'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 h-10 rounded-xl text-sm font-semibold transition-all"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.65)' }}
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
